import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Loader2, BookOpen, GraduationCap, PlaySquare, Sparkles, AlertCircle, ArrowRight } from "lucide-react";

import Header from "@/components/Header";
import { listBlogArticles, getBlogCategories } from "@/lib/api";

const CATEGORY_ORDER = ["articles", "tutorials", "videos", "case_studies"];
const CATEGORY_ICONS = {
  articles: BookOpen,
  tutorials: GraduationCap,
  videos: PlaySquare,
  case_studies: Sparkles,
};

/**
 * Public blog listing page. Articles come from an EXTERNAL MongoDB
 * database (see backend/blog.py) so this page is fully read-only and
 * safely degrades to an empty state if the DB is unreachable.
 */
export default function Blog() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // When arriving on the blog listing, always start at the top · react-router
  // preserves scroll position across client-side navigation by default.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    Promise.all([
      listBlogArticles({ category: category === "all" ? null : category }),
      getBlogCategories(),
    ])
      .then(([list, cats]) => {
        if (cancelled) return;
        setItems(list.items || []);
        const map = {};
        (cats.categories || []).forEach((c) => (map[c.id] = c.count));
        setCounts(map);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category, i18n.language]);

  const totalAll = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts]
  );

  return (
    <div className="min-h-screen">
      {/* SEO · hreflang tags per Google multilingual guidelines.
          https://developers.google.com/search/docs/specialty/international/localized-versions
          Same `/blog` URL serves both languages via ?lang=<code> which
          is honoured by i18n/index.js on load. */}
      <Helmet>
        <title>{`${t("blog.hero_title")} · Spectraloom`}</title>
        <meta name="description" content={t("blog.hero_subtitle")} />
        <link rel="canonical" href="https://spectraloom.app/blog" />
        <link rel="alternate" hrefLang="it" href="https://spectraloom.app/blog?lang=it" />
        <link rel="alternate" hrefLang="en" href="https://spectraloom.app/blog?lang=en" />
        <link rel="alternate" hrefLang="x-default" href="https://spectraloom.app/blog" />
      </Helmet>
      <Header />
      <main
        className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 relative z-10"
        data-testid="blog-page"
      >
        {/* Hero panel · matches the content-hub design (radial violet→teal
            gradient, kicker in cyan, huge display headline, off-white pill CTA). */}
        <section
          data-testid="blog-hero"
          className="fade-up mb-10 rounded-3xl overflow-hidden relative border border-white/5 px-8 sm:px-12 py-14 sm:py-20"
          style={{
            background:
              "radial-gradient(120% 140% at 0% 0%, rgba(139,92,246,0.35) 0%, rgba(88,28,135,0.25) 40%, rgba(15,23,42,0.6) 75%, rgba(6,20,30,0.85) 100%)",
          }}
        >
          <div className="max-w-xl relative z-10">
            <div className="tiny-caps text-[#22d3ee] mb-5 tracking-[0.25em]">
              {t("blog.hero_kicker")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-white mb-5">
              {t("blog.hero_title")}
            </h1>
            <p className="text-[color:var(--fg-secondary)] text-base sm:text-lg leading-relaxed mb-8">
              {t("blog.hero_subtitle")}
            </p>
            <Link
              to="/"
              data-testid="blog-hero-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#f1f0e8] hover:bg-white text-[#0b0d10] font-semibold px-5 py-2.5 text-sm transition-colors no-underline"
            >
              {t("blog.hero_cta")}
              <ArrowRight size={16} />
            </Link>
          </div>
        </section>

        {/* Category filters */}
        <div
          className="flex flex-wrap gap-2 mb-8"
          data-testid="blog-category-tabs"
        >
          <CategoryPill
            active={category === "all"}
            onClick={() => setCategory("all")}
            label={t("blog.all")}
            count={totalAll}
            testId="blog-cat-all"
          />
          {CATEGORY_ORDER.map((id) => {
            const Icon = CATEGORY_ICONS[id];
            return (
              <CategoryPill
                key={id}
                active={category === id}
                onClick={() => setCategory(id)}
                label={t(`blog.categories.${id}`)}
                count={counts[id] || 0}
                Icon={Icon}
                testId={`blog-cat-${id}`}
              />
            );
          })}
        </div>

        {loading ? (
          <div
            className="flex items-center gap-2 text-[color:var(--fg-muted)] text-sm"
            data-testid="blog-loading"
          >
            <Loader2 className="animate-spin" size={16} />
            {t("blog.loading")}
          </div>
        ) : error ? (
          <UnavailableState />
        ) : items.length === 0 ? (
          <EmptyState hasCategory={category !== "all"} />
        ) : (
          <div
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="blog-grid"
          >
            {items.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function CategoryPill({ active, onClick, label, count, Icon, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
        active
          ? "bg-[#f1f0e8] text-[#0b0d10] border-transparent"
          : "bg-white/[0.03] text-[color:var(--fg-secondary)] hover:text-white border-white/10 hover:border-white/20"
      }`}
    >
      {Icon && <Icon size={13} />}
      <span>{label}</span>
      <span className="text-[10px] tiny-caps opacity-60">{count}</span>
    </button>
  );
}

function ArticleCard({ article }) {
  const { t, i18n } = useTranslation();
  const date = article.published_at
    ? new Date(article.published_at).toLocaleDateString(
        i18n.language === "en" ? "en-US" : "it-IT",
        { day: "numeric", month: "short", year: "numeric" }
      )
    : null;

  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group rounded-2xl overflow-hidden bg-white/[0.02] border border-white/10 hover:border-white/25 hover:bg-white/[0.04] transition-all no-underline"
      data-testid={`blog-card-${article.slug}`}
    >
      {article.cover_image ? (
        <div className="aspect-[16/10] overflow-hidden bg-white/5">
          <img
            src={article.cover_image}
            alt=""
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
            draggable={false}
          />
        </div>
      ) : (
        <div className="aspect-[16/10] bg-gradient-to-br from-[#8B5CF6]/20 to-transparent flex items-center justify-center">
          <BookOpen size={32} className="text-[#8B5CF6]/60" />
        </div>
      )}
      <div className="p-6 flex flex-col gap-3">
        <div className="tiny-caps text-[#22d3ee] tracking-widest text-[10px]">
          {t(`blog.categories.${article.category}`)}
        </div>
        <h2 className="font-display text-xl font-bold text-white leading-tight line-clamp-2">
          {article.title || article.slug}
        </h2>
        {article.excerpt && (
          <p className="text-sm text-[color:var(--fg-secondary)] line-clamp-3 leading-relaxed">
            {article.excerpt}
          </p>
        )}
        {date && (
          <div className="text-xs text-[color:var(--fg-muted)] mt-2">
            {date}
          </div>
        )}
      </div>
    </Link>
  );
}

function EmptyState({ hasCategory }) {
  const { t } = useTranslation();
  return (
    <div
      className="card p-10 text-center max-w-xl mx-auto"
      data-testid="blog-empty"
    >
      <BookOpen size={28} className="mx-auto mb-3 text-[color:var(--brand)]" />
      <div className="text-white font-semibold mb-2">{t("blog.empty_title")}</div>
      <p className="text-sm text-[color:var(--fg-secondary)]">
        {hasCategory ? t("blog.empty_category") : t("blog.empty_body")}
      </p>
    </div>
  );
}

function UnavailableState() {
  const { t } = useTranslation();
  return (
    <div
      className="card p-10 text-center max-w-xl mx-auto border-l-4 border-l-amber-400/50"
      data-testid="blog-unavailable"
    >
      <AlertCircle size={28} className="mx-auto mb-3 text-amber-300" />
      <div className="text-white font-semibold mb-2">
        {t("blog.unavailable_title")}
      </div>
      <p className="text-sm text-[color:var(--fg-secondary)]">
        {t("blog.unavailable_body")}
      </p>
    </div>
  );
}
